// Package imports:
import React from 'react';
// Component imports:
import LmdTable from '../../../ui-elements/Table/LmdTable';
import Link from '../../../ui-elements/Link/Link';
import CalculatedColoredNumber from '../../../ui-elements/CalculatedColoredNumber/CalculatedColoredNumber';
import Avatar, { AvatarNames } from '../../../ui-elements/Avatar/Avatar';
import MiniGraph from '../../../ui-elements/MiniGraph/MiniGraph';
// Service imports:
import { convertToPercentage, formatNumber, displayQuantityShorthand, getLogoSymbol } from '../../../services/utils';
import { useApiLmdData } from '../../../services/apiHooks';
// Type imports:
import { IApiLmdSnapshot, IApiLmdIndex, IOmxAndSnapshot } from '../../../types/HlutabrefTypes';
import { ApiLmdData, IDefaultProps } from '../../../types/Types';

type Props = IDefaultProps;

const OllHlutabref: React.FC<Props>= ({
    refreshRateMs,
    accessToken,
}) => {
    const [ allEquities, prevAllEquities] = useApiLmdData<IApiLmdSnapshot[]>(
        '/v1/market_data/v1/keldan/exchange/*/market/*/symbol/*/delayed_equities',
        accessToken,
        refreshRateMs
    );
    const [ omx, prevOmx ] = useApiLmdData<IApiLmdIndex[]>(
        'v1/market_data/v1/keldan/index/OMXI10/snapshot',
        accessToken,
        refreshRateMs
    );

    const combinedData: IOmxAndSnapshot[] | null = (omx.data === null || allEquities.data === null)
    ? null
    : [
        ...omx.data.map(omx => ({
            symbol: omx.Symbol,
            last_price: omx.CurrentValue,
            intraday_per_change: omx.DiffDayPer,
            bid: null,
            ask: null,
            intraday_accumulated_turnover: null
        })),
        ...allEquities.data
    ];

    const apiData: ApiLmdData<IOmxAndSnapshot[]> = {
        data: combinedData,
        error: allEquities.error ?? omx.error ?? null
    }


    const combinedPrevData: IOmxAndSnapshot[] = [
        ...(prevOmx.data?.map(omx => ({
            symbol: omx.Symbol,
            last_price: omx.CurrentValue,
            intraday_per_change: omx.DiffDayPer,
            bid: null,
            ask: null,
            intraday_accumulated_turnover: null
        })) ?? []),
        ...(prevAllEquities?.data ?? [])
    ];

    return (
        <LmdTable 
            apiData={apiData}
            columns={[{
                title: 'Auðkenni',
                dataAccessor: ({symbol}) => (
                    <div className="avatar-and-link avatar-text-link">
                        <a href={`/Markadir/${symbol === 'OMXI10' ? 'Visitolur' : 'Hlutabref'}/${symbol}`} aria-label={`${symbol === 'OMXI10' ? 'Vísitölur -' : 'Hlutabréf -'} ${symbol}`}>
                            {(symbol === null)
                                ? null
                                : (
                                    <Avatar
                                        size='sm'
                                        backgroundGrey
                                        name={(symbol === 'OMXI10') ? 'OMX' : getLogoSymbol(symbol.toUpperCase()) as AvatarNames}
                                        noPadding={(symbol === 'OMXI10')}
                                    />
                                )
                            }
                        </a>
                        <Link url={`/Markadir/${symbol === 'OMXI10' ? 'Visitolur' : 'Hlutabref'}/${symbol}`} linkSize='13'>
                            {symbol}
                        </Link>
                    </div>
                ),
                textAlign: 'left',
                simpleSortable: ({symbol}) => symbol
            }, {
                title: 'Verð',
                dataAccessor: ({last_price, symbol}) => {
                    return (
                        <td style={{ padding: 0 }}>
                            <CalculatedColoredNumber 
                                hasBackground
                                currentValue={last_price}
                                previousValue={combinedPrevData.find(pd => pd.symbol === symbol)?.last_price ?? null}
                            >
                                {formatNumber(last_price)}
                            </CalculatedColoredNumber>
                        </td>
                    )
                },
                overrideTd: true,
                simpleSortable: ({last_price}) => last_price
            }, {
                title: 'Breyting',
                dataAccessor: ({intraday_per_change, symbol}) => {
                    return (
                        <td style={{ padding: 0 }}>
                            <CalculatedColoredNumber
                                hasColor
                                hasBackground
                                currentValue={intraday_per_change}
                                previousValue={combinedPrevData.find(pd => pd.symbol === symbol)?.intraday_per_change ?? null}
                            >
                                {convertToPercentage(intraday_per_change, true)}
                            </CalculatedColoredNumber>
                        </td>
                    )
                },
                overrideTd: true,
                simpleSortable: ({intraday_per_change}) => intraday_per_change
            }, {
                title: 'Sl. 3 Mán',
                dataAccessor: ({symbol}) => (symbol) && (
                    <MiniGraph
                        symbol={symbol}
                        folder={(symbol === 'OMXI10') ? 'Indices' : 'Tradables'}
                    />
                )
            }, {
                title: 'Kaup',
                dataAccessor: ({bid, symbol}) => {
                    return (
                        <td style={{ padding: 0 }}>
                            <CalculatedColoredNumber
                                hasBackground
                                currentValue={bid}
                                previousValue={combinedPrevData.find(pd => pd.symbol === symbol)?.bid ?? null}>
                                {formatNumber(bid)}
                            </CalculatedColoredNumber>
                        </td>
                    )
                },
                overrideTd: true,
                simpleSortable: ({bid}) => bid
            }, {
                title: 'Sala',
                dataAccessor: ({ask, symbol}) => {
                    return (
                        <td style={{ padding: 0}}>
                            <CalculatedColoredNumber
                                hasBackground
                                currentValue={ask}
                                previousValue={combinedPrevData.find(pd => pd.symbol === symbol)?.ask ?? null}
                                >
                                {formatNumber(ask)}
                            </CalculatedColoredNumber>
                        </td>
                    )
                },
                overrideTd: true,
                simpleSortable: ({ask}) => ask
            }, {
                title: 'Velta',
                dataAccessor: ({intraday_accumulated_turnover}) => displayQuantityShorthand(intraday_accumulated_turnover),
                simpleSortable: ({intraday_accumulated_turnover}) => intraday_accumulated_turnover
            }]}
        />
    );
}

export default OllHlutabref;